import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clientActions } from '@core/client/client.action';
import type {
  Client,
  ClientPeriodBill,
  PayRunCycleLogStatus,
} from '@core/generated/gql-types';
import { payrollActions } from '@core/payroll/payroll.action';
import type { PayRunCycleLogWithReferences } from '@core/payroll/payroll.type';
import { mapWithConcurrency } from '@core/util/concurrency.util';
import type { DateRangeValue } from '@/components/DateRangePicker';
import { MS_PER_DAY } from '@/modules/payroll/utils/payrollFormat.utils';

const DAYS_PER_YEAR = 365;
/** Pay runs are queued; give the backend time before refetching. */
const CYCLE_LOG_REFETCH_DELAY_MS = 30000;

export type RunPayCycleOptions = {
  shouldSave: boolean;
  allOrNothingMode: boolean;
  attemptToFixErrors: boolean;
};

export type CycleStatusFilter = PayRunCycleLogStatus | 'ALL';

/** A year either side of today, so recent and upcoming cycles both show. */
const getDefaultCyclePeriod = (): DateRangeValue => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    start: new Date(today.valueOf() - DAYS_PER_YEAR * MS_PER_DAY),
    end: new Date(today.valueOf() + (DAYS_PER_YEAR + 1) * MS_PER_DAY - 1),
  };
};

export const usePayRunCycleLogs = (tenantId: string | null) => {
  const [cycleLogs, setCycleLogs] = useState<PayRunCycleLogWithReferences[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<CycleStatusFilter>('ALL');
  const [period, setPeriod] = useState<DateRangeValue>(getDefaultCyclePeriod);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);

  const [cycleDetailsById, setCycleDetailsById] = useState<
    Record<string, PayRunCycleLogWithReferences>
  >({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [clientBillsById, setClientBillsById] = useState<
    Record<string, ClientPeriodBill>
  >({});
  const [billClientsById, setBillClientsById] = useState<
    Record<string, Client>
  >({});
  const [isLoadingBills, setIsLoadingBills] = useState(false);

  const [forceSyncingIds, setForceSyncingIds] = useState<Set<string>>(
    () => new Set(),
  );
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const fetchCycleLogs = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    if (!tenantId) {
      setCycleLogs([]);
      setSelectedCycleId(null);
      setLastSyncedAt(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const logs = await payrollActions.getPayRunCycleLogs({
        tenantId,
        periodStartISO: period.start.toISOString(),
        periodEndISO: period.end.toISOString(),
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setCycleLogs(logs);
      setCycleDetailsById({});
      setSelectedCycleId((current) =>
        current && logs.some((log) => log.id === current)
          ? current
          : (logs[0]?.id ?? null),
      );
      setLastSyncedAt(Date.now());
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(
          err instanceof Error ? err.message : 'Unable to load pay runs.',
        );
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [tenantId, period, statusFilter]);

  useEffect(() => {
    void fetchCycleLogs();
  }, [fetchCycleLogs]);

  useEffect(
    () => () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    },
    [],
  );

  // The list query omits nested day sequences, job classifications and item
  // clients; load the full cycle once it is selected.
  useEffect(() => {
    if (!selectedCycleId || cycleDetailsById[selectedCycleId]) {
      return;
    }
    let cancelled = false;
    setIsLoadingDetails(true);
    setDetailsError(null);
    payrollActions
      .getPayRunCycleLog(selectedCycleId)
      .then((detail) => {
        if (!cancelled && detail) {
          setCycleDetailsById((previous) => ({
            ...previous,
            [selectedCycleId]: detail,
          }));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailsError(
            err instanceof Error
              ? err.message
              : 'Unable to load cycle details.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingDetails(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCycleId, cycleDetailsById]);

  const activeCycle = useMemo(() => {
    if (!selectedCycleId) {
      return null;
    }
    return (
      cycleDetailsById[selectedCycleId] ??
      cycleLogs.find((log) => log.id === selectedCycleId) ??
      null
    );
  }, [cycleDetailsById, cycleLogs, selectedCycleId]);

  const activeClientBillIdsKey = activeCycle?.clientBillIds.join(',') ?? '';

  // Client bills (and their client names) for the active cycle only.
  useEffect(() => {
    const billIds = activeClientBillIdsKey
      ? activeClientBillIdsKey.split(',')
      : [];
    if (!billIds.length) {
      return;
    }
    let cancelled = false;
    setIsLoadingBills(true);
    const load = async () => {
      const bills = await payrollActions.getClientPeriodBillsByIds(billIds);
      const clientIds = Array.from(
        new Set(Object.values(bills).map((bill) => bill.clientId)),
      ).filter(Boolean);
      const clients = await mapWithConcurrency(clientIds, (id) =>
        clientActions.getClientById(id).catch(() => null),
      );
      if (cancelled) {
        return;
      }
      setClientBillsById((previous) => ({ ...previous, ...bills }));
      setBillClientsById((previous) =>
        clients.reduce<Record<string, Client>>(
          (acc, client) => (client ? { ...acc, [client.id]: client } : acc),
          previous,
        ),
      );
    };
    load()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setIsLoadingBills(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeClientBillIdsKey]);

  const runPayCycle = useCallback(
    async (options: RunPayCycleOptions) => {
      if (!tenantId) {
        throw new Error('No tenant selected.');
      }
      const result = await payrollActions.initiatePayRunForTenant({
        tenantId,
        ...options,
      });
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      refetchTimeoutRef.current = setTimeout(() => {
        void fetchCycleLogs();
      }, CYCLE_LOG_REFETCH_DELAY_MS);
      return result;
    },
    [tenantId, fetchCycleLogs],
  );

  const forceSyncCycle = useCallback(
    async (cycleId: string) => {
      setForceSyncingIds((current) => new Set(current).add(cycleId));
      try {
        const queued = await payrollActions.forceSyncPayRunCycleLog(cycleId);
        if (!queued) {
          throw new Error('Sync was not queued.');
        }
        await fetchCycleLogs();
      } finally {
        setForceSyncingIds((current) => {
          const next = new Set(current);
          next.delete(cycleId);
          return next;
        });
      }
    },
    [fetchCycleLogs],
  );

  return {
    cycleLogs,
    isLoading,
    error,
    lastSyncedAt,
    statusFilter,
    setStatusFilter,
    period,
    setPeriod,
    selectedCycleId,
    setSelectedCycleId,
    activeCycle,
    isLoadingDetails,
    detailsError,
    clientBillsById,
    billClientsById,
    isLoadingBills,
    forceSyncingIds,
    refresh: fetchCycleLogs,
    runPayCycle,
    forceSyncCycle,
  };
};

export type PayRunCycleLogsState = ReturnType<typeof usePayRunCycleLogs>;
